import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// Danish address rules (adressebekendtgørelsen §§ 16-24) and the column limits of the CSO location table
export const ADDRESS_MAX_LENGTH = {
  street: 40,
  letter: 10,
  floor: 20,
  door: 20,
  town: 40,
} as const;

export const HOUSE_NUMBER_MIN = 1;
export const HOUSE_NUMBER_MAX = 999;

// House letter: exactly one capital letter A-Z, e.g. "A", "B"
const HOUSE_LETTER_PATTERN = /^[A-Z]$/;
// Floor: "st" (ground floor), "kl" (basement), "k2"-"k9" (lower basements) or "1"-"99" (no leading zero)
const FLOOR_PATTERN = /^(st|kl|k[2-9]|[1-9]\d?)$/;
// Door: 1-4 characters of a-z, 0-9 and "-". Covers "tv", "th", "mf", numbers 1-9999 and systems like "a101" or "b-2"
const DOOR_PATTERN = /^[a-z0-9-]{1,4}$/;
// Postal code: exactly 4 digits, e.g. "8000"
const POSTAL_CODE_PATTERN = /^\d{4}$/;

export function normalizeHouseLetter(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

// "st." -> "st", "t.v." -> "tv", "TH" -> "th"
export function normalizeFloorOrDoor(value: string | null | undefined): string | null {
  const cleaned = (value ?? '').replace(/[\s.]/g, '').toLowerCase();
  return cleaned === '' ? null : cleaned;
}

function patternValidator(pattern: RegExp, normalize: (value: string) => string | null, errorKey: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = normalize(String(control.value ?? ''));
    if (!value) {
      return null;
    }
    return pattern.test(value) ? null : { [errorKey]: true };
  };
}

export const houseLetterValidator = patternValidator(HOUSE_LETTER_PATTERN, normalizeHouseLetter, 'houseLetter');
export const floorValidator = patternValidator(FLOOR_PATTERN, normalizeFloorOrDoor, 'floor');
export const doorValidator = patternValidator(DOOR_PATTERN, normalizeFloorOrDoor, 'door');
export const postalCodeValidator = patternValidator(POSTAL_CODE_PATTERN, value => value.trim(), 'postalCode');

export const houseNumberValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = String(control.value ?? '').trim();
  if (!value) {
    return null;
  }
  const number = Number(value);
  return Number.isInteger(number) && number >= HOUSE_NUMBER_MIN && number <= HOUSE_NUMBER_MAX ? null : { houseNumber: true };
};

/**
 * Only applies the validator when the address is entered without DAR validation.
 * DAR addresses are authoritative and may contain values that are no longer allowed for new addresses.
 */
export function manualAddressOnly(validator: ValidatorFn): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const withoutDar = control.parent?.parent?.get('addressWithoutDARValidation')?.value === true;
    return withoutDar ? validator(control) : null;
  };
}
